import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function Inventory() {
  const navigate = useNavigate();
  
  // Get user role for permissions
  const userRole = localStorage.getItem("role");
  const isStaff = userRole === "Staff";
  
  // Get tab from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const tabFromUrl = parseInt(searchParams.get('tab') || '0');
  
  const [tab, setTab] = useState(tabFromUrl); // 0 = Ready Made, 1 = Customized, 2 = Raw Materials
  const [query, setQuery] = useState("");
  const [productType, setProductType] = useState("");
  const [productTypes, setProductTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchProductTypes();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInventory();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [tab, productType, query, page]);

  const normalizeProductTypeLabel = (productType) => {
    if (productType === "Salwar Suit" || productType === "Salwar Suits") {
      return "Apparels";
    }
    return productType;
  };

  const fetchProductTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/product-types`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProductTypes(data.types || []);
      }
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      if (tab === 2) {
        // Fetch Raw Materials
        const url = new URL(`${API_BASE_URL}/api/inventory/raw-materials`);
        if (productType) url.searchParams.append("productType", productType);
        if (query) url.searchParams.append("search", query);
        url.searchParams.append("page", page);
        url.searchParams.append("limit", ITEMS_PER_PAGE);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const allMaterials = data.materials || [];
          if (data.totalPages) {
            // Server-side pagination active
            setRawMaterials(allMaterials);
            setTotalPages(data.totalPages);
          } else {
            // Fallback: server returned all items, paginate client-side
            const total = Math.ceil(allMaterials.length / ITEMS_PER_PAGE);
            setTotalPages(total || 1);
            const start = (page - 1) * ITEMS_PER_PAGE;
            setRawMaterials(allMaterials.slice(start, start + ITEMS_PER_PAGE));
          }
        }
      } else {
        // Fetch Products
        const category = tab === 0 ? "Ready Made" : "Customized";
        const url = new URL(`${API_BASE_URL}/api/inventory/products`);
        url.searchParams.append("category", category);
        if (productType) url.searchParams.append("productType", productType);
        if (query) url.searchParams.append("search", query);
        url.searchParams.append("page", page);
        url.searchParams.append("limit", ITEMS_PER_PAGE);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const allProducts = data.products || [];
          if (data.totalPages) {
            // Server-side pagination active
            setProducts(allProducts);
            setTotalPages(data.totalPages);
          } else {
            // Fallback: server returned all items, paginate client-side
            const total = Math.ceil(allProducts.length / ITEMS_PER_PAGE);
            setTotalPages(total || 1);
            const start = (page - 1) * ITEMS_PER_PAGE;
            setProducts(allProducts.slice(start, start + ITEMS_PER_PAGE));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (code, name, isRawMaterial = false) => {
    setItemToDelete({ code, name, isRawMaterial });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const endpoint = itemToDelete.isRawMaterial
        ? `${API_BASE_URL}/api/inventory/raw-materials/${itemToDelete.code}`
        : `${API_BASE_URL}/api/inventory/products/${itemToDelete.code}`;

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success("Item deleted successfully");
        fetchInventory();
      } else {
        toast.error("Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleEdit = (code, isRawMaterial = false) => {
    if (isRawMaterial) {
      navigate(`/inventory/raw-materials/edit/${code}?tab=2`);
    } else {
      navigate(`/inventory/products/edit/${code}?tab=${tab}`);
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { label: "Out of Stock", color: "error" };
    if (quantity <= 5) return { label: "Low Stock", color: "warning" };
    return { label: "In Stock", color: "success" };
  };

  const getTabLabel = () => {
    if (tab === 0) return "Ready Made Products";
    if (tab === 1) return "Customized Products";
    return "Raw Materials";
  };

  const filteredList = tab === 2 ? rawMaterials : products;

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            letterSpacing: 3,
            color: "#BC965E",
            textAlign: "center",
            mb: 4,
            textDecoration: "underline",
            textUnderlineOffset: 12,
            textDecorationThickness: 2,
          }}
        >
          INVENTORY
        </Typography>

        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* SEARCH BAR */}
          <TextField
            fullWidth
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name or code..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#C4A574" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#3a3a3a",
                borderRadius: 2,
                border: "1px solid rgba(196, 165, 116, 0.2)",
                color: "#C4A574",
                "& fieldset": {
                  borderColor: "transparent",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(196, 165, 116, 0.3)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#C4A574",
                },
              },
              "& .MuiOutlinedInput-input": {
                color: "#C4A574",
                "&::placeholder": {
                  color: "rgba(196, 165, 116, 0.6)",
                  opacity: 1,
                },
              },
            }}
          />

          {/* TABS */}
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              setProductType("");
              setPage(1);
            }}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              mb: 3,
              "& .MuiTab-root": {
                color: "#999",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                textTransform: "uppercase",
                letterSpacing: 0.5,
                "&.Mui-selected": {
                  color: "#C4A574",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#C4A574",
                height: 3,
              },
            }}
          >
            <Tab label="Ready Made" />
            <Tab label="Customized" />
            {!isStaff && <Tab label="Raw Materials" />}
          </Tabs>

          {/* FILTER AND ADD BUTTON */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap" }}>
            {tab !== 2 && (
              <FormControl sx={{ minWidth: 200, maxWidth: 280 }}>
                <InputLabel
                  shrink
                  sx={{ color: "#C4A574", "&.Mui-focused": { color: "#C4A574" } }}
                >
                  Filter by Type
                </InputLabel>
                <Select
                  value={productType}
                  onChange={(e) => {
                    setProductType(e.target.value);
                    setPage(1);
                  }}
                  label="Filter by Type"
                  notched
                  displayEmpty
                  sx={{
                    backgroundColor: "#3a3a3a",
                    color: "#C4A574",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(196, 165, 116, 0.2)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(196, 165, 116, 0.3)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#C4A574",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#C4A574",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "#2a2a2a",
                        border: "1px solid rgba(196, 165, 116, 0.2)",
                        borderRadius: 2,
                        mt: 1,
                        "& .MuiMenuItem-root": {
                          color: "#C4A574",
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(196, 165, 116, 0.1)",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "rgba(196, 165, 116, 0.2)",
                            "&:hover": {
                              backgroundColor: "rgba(196, 165, 116, 0.3)",
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: "#C4A574", fontStyle: "normal" }}>All Types</em>
                  </MenuItem>
                  {productTypes.map((type) => (
                    <MenuItem key={type.ProductTypeId} value={type.TypeName}>
                      {normalizeProductTypeLabel(type.TypeName)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isStaff && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (tab === 2) {
                    navigate("/inventory/raw-materials/new?tab=2");
                  } else if (tab === 0) {
                    navigate("/inventory/products/new?category=Ready Made&tab=0");
                  } else {
                    navigate("/inventory/products/new?category=Customized&tab=1");
                  }
                }}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  "&:hover": { bgcolor: "#B39565" },
                }}
              >
                Add {tab === 2 ? "Material" : "Product"}
              </Button>
            )}
          </Box>

          {/* LOADING */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#C4A574" }} />
            </Box>
          ) : (
            /* GRID */
            <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
              {filteredList && filteredList.length > 0 ? (
                filteredList.map((item) => {
                  const isRawMaterial = tab === 2;
                  const code = isRawMaterial ? item.MaterialId : item.ProductCode;
                  const name = isRawMaterial ? item.MaterialName : item.ProductName;
                  const displayCode = isRawMaterial ? `RM-${item.MaterialId}` : item.ProductCode;
                  const quantity = item.QuantityInStock || 0;
                  const stockStatus = getStockStatus(quantity);

                  return (
                    <Grid item key={code} xs={12} sm={6} md={4} lg={3}>
                      <Card
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: "100%",
                          minWidth: { xs: 280, sm: 300, md: 320 },
                          backgroundColor: "#2a2a2a",
                          border: "1px solid rgba(196, 165, 116, 0.2)",
                          borderRadius: 2,
                          transition: "all 0.3s ease",
                          position: "relative",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                            borderColor: "#C4A574",
                          },
                        }}
                      >
                        {/* Stock Status Chip */}
                        <Chip
                          label={stockStatus.label}
                          color={stockStatus.color}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            fontWeight: 700,
                          }}
                        />

                        {/* IMAGE */}
                        {!isRawMaterial && (
                          <CardMedia
                            component="img"
                            sx={{
                              width: "100%",
                              height: 180,
                              objectFit: "cover",
                              backgroundColor: "#1a1a1a",
                            }}
                            image={
                              item.Photo
                                ? `${API_BASE_URL}${item.Photo}`
                                : "https://via.placeholder.com/300x180?text=No+Image"
                            }
                            alt={name}
                          />
                        )}

                        {/* CONTENT */}
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            pt: isRawMaterial ? 3 : 2,
                          }}
                        >
                          {/* Code Badge */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#C4A574",
                              fontWeight: 700,
                              mb: 1,
                              fontSize: "0.75rem",
                              letterSpacing: 0.5,
                            }}
                          >
                            ID: {displayCode}
                          </Typography>

                          {/* Product Name */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              mb: 1,
                              fontSize: "1rem",
                            }}
                          >
                            {name}
                          </Typography>

                          {/* Product Type */}
                          {item.ProductType && (
                            <Chip
                              label={item.ProductType}
                              size="small"
                              sx={{
                                mb: 1,
                                backgroundColor: "rgba(196, 165, 116, 0.2)",
                                color: "#C4A574",
                                fontWeight: 600,
                              }}
                            />
                          )}

                          {/* Color - Products only */}
                          {!isRawMaterial && item.Color && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#999", mb: 1 }}
                            >
                              Color:{" "}
                              <span style={{ color: "#C4A574", fontWeight: 600 }}>
                                {item.Color}
                              </span>
                            </Typography>
                          )}

                          {/* Unit - Raw Materials only */}
                          {isRawMaterial && item.Unit && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#999", mb: 1 }}
                            >
                              Unit:{" "}
                              <span style={{ color: "#C4A574", fontWeight: 600 }}>
                                {item.Unit}
                              </span>
                            </Typography>
                          )}

                          {isRawMaterial ? (
                            // Raw Material - Show only stock
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: "rgba(196, 165, 116, 0.1)",
                                borderRadius: 1,
                                border: "1px solid rgba(196, 165, 116, 0.2)",
                              }}
                            >
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                                >
                                  Stock
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" },
                                    color: "#C4A574",
                                  }}
                                >
                                  {quantity} {item.Unit}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            // Product - Show price, purchase price, and stock
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: "rgba(196, 165, 116, 0.1)",
                                borderRadius: 1,
                                border: "1px solid rgba(196, 165, 116, 0.2)",
                              }}
                            >
                              {/* Price Row */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb:
                                    item.Category === "Ready Made" &&
                                    item.PurchasePrice
                                      ? 1
                                      : 0,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#999", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                                  >
                                    Selling Price
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      color: "#C4A574",
                                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                                    }}
                                  >
                                    ₹{parseFloat(item.Price).toFixed(2)}
                                  </Typography>
                                </Box>

                                {!isStaff &&
                                  item.Category === "Ready Made" &&
                                  item.PurchasePrice && (
                                    <Box sx={{ textAlign: "center" }}>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#999", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                                      >
                                        Purchase Price
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          color: "#888",
                                          fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                                        }}
                                      >
                                        ₹{parseFloat(item.PurchasePrice).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  )}

                                <Box sx={{ textAlign: "right" }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#999", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                                  >
                                    Stock
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                                      color: "#C4A574",
                                    }}
                                  >
                                    {quantity}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Profit Margin - Admin only */}
                              {!isStaff &&
                                item.Category === "Ready Made" &&
                                item.PurchasePrice &&
                                item.PurchasePrice > 0 && (
                                  <Box
                                    sx={{
                                      textAlign: "center",
                                      pt: 1,
                                      borderTop: "1px solid rgba(196, 165, 116, 0.2)",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#999" }}
                                    >
                                      Profit Margin
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        color: "#4caf50",
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      ₹{(parseFloat(item.Price) - parseFloat(item.PurchasePrice)).toFixed(2)}{" "}
                                      ({(((parseFloat(item.Price) - parseFloat(item.PurchasePrice)) / parseFloat(item.PurchasePrice)) * 100).toFixed(1)}%)
                                    </Typography>
                                  </Box>
                                )}
                            </Box>
                          )}

                          {/* ACTION BUTTONS */}
                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              gap: 1,
                              flexDirection: "column",
                            }}
                          >
                            {/* View Button */}
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<VisibilityIcon />}
                              onClick={() => {
                                if (isRawMaterial) {
                                  navigate(`/inventory/raw-materials/${code}`);
                                } else {
                                  navigate(`/inventory/products/${code}?tab=${tab}`);
                                }
                              }}
                              fullWidth
                              sx={{
                                bgcolor: "#C4A574",
                                color: "#000",
                                fontWeight: 600,
                                "&:hover": { bgcolor: "#B39565" },
                              }}
                            >
                              View
                            </Button>

                            {/* Edit and Delete Row - Admin only */}
                            {!isStaff && (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleEdit(code, isRawMaterial)}
                                  fullWidth
                                  sx={{
                                    borderColor: "#C4A574",
                                    color: "#C4A574",
                                    fontWeight: 600,
                                    "&:hover": {
                                      bgcolor: "rgba(196, 165, 116, 0.1)",
                                      borderColor: "#C4A574",
                                    },
                                  }}
                                >
                                  Edit
                                </Button>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteClick(code, name, isRawMaterial)}
                                  fullWidth
                                  sx={{
                                    borderColor: "#ff6b6b",
                                    color: "#ff6b6b",
                                    fontWeight: 600,
                                    "&:hover": {
                                      bgcolor: "rgba(255, 107, 107, 0.1)",
                                      borderColor: "#ff6b6b",
                                    },
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })
              ) : (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#999", mb: 1 }}
                    >
                      No {getTabLabel().toLowerCase()} found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {!isStaff
                        ? `Use the "Add ${tab === 2 ? "Material" : "Product"}" button above to create your first item`
                        : "No items available for your role"}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {/* PAGINATION */}
          {!loading && filteredList && filteredList.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4, mb: 2, gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#999" }}>
                Page {page} of {totalPages}
              </Typography>
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#C4A574",
                      borderColor: "rgba(196, 165, 116, 0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(196, 165, 116, 0.1)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#C4A574",
                        color: "#000",
                        fontWeight: 700,
                        "&:hover": {
                          backgroundColor: "#B39565",
                        },
                      },
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* Decorative Fade */}
          <Box
            sx={{
              height: 60,
              background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
              borderRadius: 2,
            }}
          />
        </Box>
      </Box>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: "1px solid #444",
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle 
          id="delete-dialog-title"
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
            borderBottom: '2px solid #ff6b6b',
            color: '#fff',
            pb: 2
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#ff6b6b' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: 'transparent' }}>
          <DialogContentText 
            id="delete-dialog-description"
            sx={{ color: '#fff' }}
          >
            Are you sure you want to delete <strong style={{ color: '#C4A574' }}>"{itemToDelete?.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          background: "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
          borderTop: '1px solid #444',
          gap: 1
        }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              color: '#C4A574',
              borderColor: '#C4A574',
              '&:hover': {
                bgcolor: 'rgba(196, 165, 116, 0.1)',
                borderColor: '#C4A574'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            autoFocus
            sx={{
              bgcolor: '#ff6b6b',
              color: '#fff',
              '&:hover': {
                bgcolor: '#ff5252'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
